import { useState } from 'react';
import {
  Autocomplete,
  Box,
  Button,
  LinearProgress,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { DataGrid, GridToolbar } from '@mui/x-data-grid';

import dealerCodeTypes from '../util/dealerCodes';

const columns = [
  { field: 'serviceAccountId', headerName: 'Account ID', width: 160 },
  { field: 'url', headerName: 'URL', flex: 1, minWidth: 200 },
  { field: 'dealerCode', headerName: 'Dealer Code', width: 160 },
];

const DealerCodeSearch = ({ requestOptions }) => {
  const [dealerCodeType, setDealerCodeType] = useState('');
  const [rows, setRows] = useState([]);
  const [progress, setProgress] = useState(0);
  const [loading, setLoading] = useState(false);

  const findDealerCode = (entries) => {
    const match = entries?.find(
      (e) => e.type?.toLowerCase() === dealerCodeType?.toLowerCase()
    );
    return match ? match.code : 'Matching dealer code not found';
  };

  const callApi = async () => {
    if (!dealerCodeType) return;
    setLoading(true);
    setProgress(0);
    setRows([]);

    try {
      const idsResponse = await fetch(
        `/account-data-distributor/v1/serviceAccounts/dealerCodeType/${dealerCodeType}/serviceAccountIds`,
        requestOptions
      );
      const ids = await idsResponse.json();

      const display = [];
      let i = 1;
      for (const item of ids) {
        const r = await fetch(
          `/account-data-distributor/v1/serviceAccounts/${item}`,
          requestOptions
        );
        const sa = await r.json();
        display.push({
          id: i,
          serviceAccountId: sa.id,
          url: sa.url,
          dealerCode: findDealerCode(sa.dealerCodes),
        });
        setProgress(Math.round((i / ids.length) * 100));
        i += 1;
      }
      setRows(display);
    } catch (err) {
      console.error('error', err);
    } finally {
      setProgress(0);
      setLoading(false);
    }
  };

  return (
    <Stack spacing={3}>
      <Typography variant="h2">Dealer Code Search</Typography>

      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="center">
        <Autocomplete
          value={dealerCodeType || null}
          onChange={(_, v) => setDealerCodeType(v ?? '')}
          options={dealerCodeTypes}
          sx={{ width: 320 }}
          size="small"
          renderInput={(params) => <TextField {...params} label="Dealer Code Type" />}
        />
        <Button
          variant="contained"
          onClick={callApi}
          disabled={!dealerCodeType || loading}
        >
          Fetch Service Accounts
        </Button>
      </Stack>

      {loading && (
        <Stack direction="row" spacing={2} alignItems="center">
          <Box sx={{ flexGrow: 1 }}>
            <LinearProgress variant="determinate" value={progress} />
          </Box>
          <Typography variant="body2" color="text.secondary">
            {progress}%
          </Typography>
        </Stack>
      )}

      <Box sx={{ height: 600 }}>
        <DataGrid
          rows={rows}
          columns={columns}
          initialState={{
            pagination: { paginationModel: { pageSize: 10 } },
          }}
          pageSizeOptions={[10, 25, 50]}
          checkboxSelection
          disableRowSelectionOnClick
          slots={{ toolbar: GridToolbar }}
          slotProps={{ toolbar: { showQuickFilter: true } }}
        />
      </Box>
    </Stack>
  );
};

export default DealerCodeSearch;
